/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2025
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { css, Part } from 'lit';
import { state } from 'lit/decorators.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import StableSelectorMixin from '../../globals/mixins/stable-selector';
import C4DContentBlock from '../content-block/content-block';
import styles from './content-block-cards.scss';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';
import MediaQueryMixin, {
  MQBreakpoints,
  MQDirs,
} from '../../component-mixins/media-query/media-query';

const { prefix, stablePrefix: c4dPrefix } = settings;

/**
 * Content block cards.
 *
 * @element c4d-content-block-cards
 */
@customElement(`${c4dPrefix}-content-block-cards`)
class C4DContentBlockCards extends MediaQueryMixin(
  StableSelectorMixin(C4DContentBlock),
  {
    [MQBreakpoints.MD]: MQDirs.MAX,
  }
) {
  @state()
  private _isMobileOrMedium = this.carbonBreakpoints.md.matches;

  /**
   * The CSS class list for the container (grid) node.
   */
  // eslint-disable-next-line class-methods-use-this
  protected _getContainerClasses(): string | ((part: Part) => void) {
    return `${prefix}--content-layout ${prefix}--content-layout--card-group`;
  }

  static get stableSelector() {
    return `${c4dPrefix}--content-block-cards`;
  }

  protected setSameHeightOnMobile() {
    requestAnimationFrame(() => {
      let maxHeightHeading = 0;
      let maxHeightParagraph = 0;

      const blockCards = this.querySelectorAll(`${c4dPrefix}-card-group-item`);

      blockCards.forEach((card) => {
        const heading = card.querySelector('c4d-card-heading') as HTMLElement;
        const para =
          card.querySelector('p') ||
          card?.shadowRoot?.querySelector('div.cds--card__copy');

        let headingHeight = 0;
        let paraHeight = 0;

        if (heading && para) {
          headingHeight = heading?.getBoundingClientRect().height;
          paraHeight = para?.getBoundingClientRect().height;
        }

        maxHeightHeading =
          maxHeightHeading > headingHeight ? maxHeightHeading : headingHeight;

        maxHeightParagraph =
          maxHeightParagraph > paraHeight ? maxHeightParagraph : paraHeight;
      });

      for (const card of blockCards) {
        const heading = card.querySelector('c4d-card-heading');
        const para =
          card.querySelector('p') ||
          card?.shadowRoot?.querySelector('div.cds--card__copy');
        if (heading && para) {
          heading.setAttribute(
            'style',
            `height: ${maxHeightHeading}px; display: block;`
          );
          para.setAttribute(
            'style',
            `height: ${maxHeightParagraph}px; display: block;`
          );
        }
      }
    });
  }

  firstUpdated() {
    window.addEventListener('load', () => {
      if (this._isMobileOrMedium) {
        this.setSameHeightOnMobile();
      }
    });
  }

  connectedCallback() {
    super.connectedCallback();

    window.onload = () => {
      const hasLogoClass = this.classList.contains('media-type__logo');
      const slot = this.shadowRoot?.querySelector('slot:not([name])');

      if (slot && hasLogoClass) {
        (slot as HTMLSlotElement)?.assignedElements().forEach((element) => {
          element.classList.add('media-type-block-cards__logo');
        });
      }
    };
  }

  // `styles` here is a `CSSResult` generated by custom WebPack loader
  static get styles() {
    return css`
      ${super.styles}${styles}
    `;
  }
}

console.warn(
  'The content-block-cards component has been deprecated in favor of the content-section/block and card-group component. ' +
    'See content-section/block and card-group documentation for more information.'
);

/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DContentBlockCards;
